import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowRight, FiStar, FiTruck, FiShield, FiHeart, FiChevronLeft, FiChevronRight, FiShare2, FiArrowUp, FiVideo, FiMessageCircle } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import ImageCarousel from '../components/ImageCarousel';
import axios from 'axios';
import { openWhatsApp, openWhatsAppVideoCall, shareContent, scrollToTop } from '../utils/contact';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedPriceFilter, setSelectedPriceFilter] = useState('10k');
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const [message, setMessage] = useState('');
  const [newArrivals, setNewArrivals] = useState([]);
  const [loadingArrivals, setLoadingArrivals] = useState(true);
  const [celebrateProducts, setCelebrateProducts] = useState([]);
  const [loadingCelebrate, setLoadingCelebrate] = useState(true);

  const handleAddToCart = async (product) => {
    const result = await addToCart(product.id, 1, null, null, product);

    if (result.success) {
      setMessage('Item added to cart successfully!');
      setTimeout(() => setMessage(''), 3000);

      window.dispatchEvent(new CustomEvent('openCartSidebar'));
    } else {
      setMessage(result.message);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const fetchNewArrivals = async () => {
    try {
      setLoadingArrivals(true);
      const response = await axios.get('/api/products/new-arrivals');
      setNewArrivals(response.data.products);
    } catch (error) {
      console.error('Error fetching new arrivals:', error);
      setMessage('Error loading new arrivals');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoadingArrivals(false);
    }
  };

  const fetchCelebrateProducts = async (priceFilter = '10k') => {
    try {
      setLoadingCelebrate(true);
      const response = await axios.get(`/api/products/celebrate?priceFilter=${priceFilter}`);

      const products = response.data.products || [];
      console.log(`Received ${products.length} products from API`);

      const limitedProducts = products.slice(0, 8);
      console.log(`Setting ${limitedProducts.length} products in state`);

      setCelebrateProducts(limitedProducts);
    } catch (error) {
      console.error('Error fetching celebrate products:', error);
      setMessage('Error loading celebrate products');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoadingCelebrate(false);
    }
  };

  const carouselSlides = [
    {
      id: 1,
      image: "https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761847331/WhatsApp_Image_2025-10-30_at_23.18.07_aef543c9_nmetje.jpg",
      position: "center 8%",
      title: "Premium Silk Collection",
      subtitle: "Handwoven with Love, Crafted for Royalty",
      badge: "New Arrival",
      description: "Discover our exquisite collection of handwoven silk sarees, each piece telling a story of tradition and elegance."
    },
    {
      id: 2,
      image: "https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761847330/WhatsApp_Image_2025-10-30_at_23.18.08_022c8b90_tyau3h.jpg",
      position: "center 20%",
      title: "Designer Sarees",
      subtitle: "Where Tradition Meets Modern Elegance",
      badge: "Best Seller",
      description: "Contemporary designs that celebrate the timeless beauty of Indian craftsmanship with a modern twist."
    },
    {
      id: 3,
      image: "https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761847330/WhatsApp_Image_2025-10-30_at_23.18.08_1662bb9a_jl5kko.jpg",
      position: "center 1%",
      title: "Bridal Collection",
      subtitle: "Make Your Special Day Unforgettable",
      badge: "Exclusive",
      description: "Stunning bridal sarees that will make you the center of attention on your most important day."
    },
    {
      id: 4,
      image: "https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761847330/WhatsApp_Image_2025-10-30_at_23.18.08_03778ade_hhvgrl.jpg",
      position: "center 5%",
      title: "Traditional Weaves",
      subtitle: "Heritage Craftsmanship at its Finest",
      badge: "Heritage",
      description: "Authentic traditional weaves from master craftsmen, preserving centuries-old techniques."
    },
    {
      id: 5,
      image: "https://cliosilks.com/cdn/shop/files/Untitled-1-2.jpg?v=1739348317&width=2400",
      position: "center center",
      title: "Party Wear",
      subtitle: "Elegant Evening Ensembles",
      badge: "Trending",
      description: "Glamorous sarees perfect for special occasions and evening events."
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prevSlide) =>
        prevSlide === carouselSlides.length - 1 ? 0 : prevSlide + 1
      );
    }, 5000);

    return () => clearInterval(timer);
  }, [carouselSlides.length]);

  useEffect(() => {
    fetchNewArrivals();
    fetchCelebrateProducts(selectedPriceFilter);
  }, []);

  const handlePriceFilterChange = (filter) => {
    setSelectedPriceFilter(filter);
    fetchCelebrateProducts(filter);
  };

  const nextSlide = () => {
    setCurrentSlide(currentSlide === carouselSlides.length - 1 ? 0 : currentSlide + 1);
  };

  const prevSlide = () => {
    setCurrentSlide(currentSlide === 0 ? carouselSlides.length - 1 : currentSlide - 1);
  };

  const handleSharePage = async () => {
    const result = await shareContent({
      title: 'Shanti Silk House',
      text: 'Discover beautiful silk sarees at Shanti Silk House'
    });
    if (result.success && result.method !== 'share') {
      setMessage('Link copied to clipboard!');
      setTimeout(() => setMessage(''), 2500);
    }
  };

  const handleWhatsAppContact = () => {
    openWhatsApp('Hi! I would like to know more about sarees at Shanti Silk House.');
  };

  const handleVideoCall = () => {
    openWhatsAppVideoCall();
  };

  const showcaseSlides = [
    {
      id: 's1',
      image: 'https://images.unsplash.com/photo-1618901185975-d59f7091bcfe?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8d29tYW4lMjBpbiUyMHNhcmVlfGVufDB8fDB8fHww',
      title: 'ELEGANT SILK SAREES',
      tagline: 'Timeless Beauty, Modern Grace'
    },
    {
      id: 's2',
      image: 'https://t4.ftcdn.net/jpg/14/92/02/63/360_F_1492026312_spPMOxAHC3jvNvsF43OUPZDPNzLhsqIq.jpg',
      title: 'TRADITIONAL WEAVES',
      tagline: 'Heritage Craftsmanship'
    },
    {
      id: 's3',
      image: 'https://t4.ftcdn.net/jpg/09/72/20/43/360_F_972204343_qxjJTNMhsdMljBCkQpxLslJsklCIDaFy.jpg',
      title: 'BRIDAL COLLECTION',
      tagline: 'Your Perfect Wedding Day'
    }
  ];

  const [showcaseIndex, setShowcaseIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setShowcaseIndex((prev) => (prev + 1) % showcaseSlides.length);
    }, 5000);
    return () => clearInterval(t);
  }, [showcaseSlides.length]);

  return (
    <div className="home">
      { }
      {message && (
        <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      { }
      <div className="side-utilities">
        <button className="utility-btn" title="Share" type="button" aria-label="Share this page" onClick={handleSharePage}>
          <FiShare2 />
        </button>
        <button className="utility-btn" title="Scroll to Top" type="button" aria-label="Scroll to top" onClick={scrollToTop}>
          <FiArrowUp />
        </button>
        <button className="utility-btn video-btn" title="Video Call" type="button" aria-label="Start video call shopping" onClick={handleVideoCall}>
          <FiVideo />
        </button>
        <button className="utility-btn whatsapp-btn" title="WhatsApp" type="button" aria-label="Contact on WhatsApp" onClick={handleWhatsAppContact}>
          <FiMessageCircle />
        </button>
      </div>

      { }
      <section className="carousel-section">
        <div className="carousel-container">
          <div className="carousel-slides" style={{ transform: `translateX(-${currentSlide * 20}%)` }}>
            {carouselSlides.map((slide, index) => (
              <div key={slide.id} className="carousel-slide">
                <div className="slide-image">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    loading={index === 0 ? "eager" : "lazy"}
                    style={{ objectPosition: slide.position || 'center center' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />

                </div>
              </div>
            ))}
          </div>

          { }
          <button className="carousel-nav prev" type="button" aria-label="Previous slide" onClick={prevSlide}>
            <FiChevronLeft />
          </button>
          <button className="carousel-nav next" type="button" aria-label="Next slide" onClick={nextSlide}>
            <FiChevronRight />
          </button>

          { }
          <div className="carousel-dots">
            {carouselSlides.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                type="button"
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="new-arrivals">
        <div className="container">
          <h2 className="arrivals-title">New Arrivals In Store</h2>
          {loadingArrivals ? (
            <div className="arrivals-loading">
              <p>Loading new arrivals...</p>
            </div>
          ) : newArrivals.length === 0 ? (
            <div className="arrivals-empty">
              <p>No new arrivals available at the moment.</p>
            </div>
          ) : (
            <div className="arrivals-grid">
              {newArrivals.map(product => (
                <div
                  key={product.id}
                  className="arrival-item"
                  onClick={() => navigate(`/product/${product.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="arrival-image">
                    <ImageCarousel
                      images={product.images && product.images.length > 0 ? product.images : ['https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761476953/Screenshot_2025-10-26_163521_xzl1r4.svg']}
                      autoPlay={true}
                      interval={4000}
                      showThumbnails={false}
                      showControls={false}
                      className="arrival-carousel"
                    />
                  </div>
                  <div className="arrival-info">
                    <div className="arrival-title">{product.name || 'Silk Saree'}</div>
                    <div className="arrival-price">₹{product.price.toLocaleString()}</div>
                    <div className="arrival-actions-wrapper">
                      <div className="arrival-actions">
                        <a
                          className="icon-btn whatsapp"
                          href={`https://wa.me/919591128327?text=${encodeURIComponent(
                            'Hi! I am interested in ' + product.name +
                            ' priced at ₹' + product.price.toLocaleString() +
                            '\n' + window.location.origin + '/product/' + (product.slug || product.id)
                          )}`}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Chat on WhatsApp"
                          title="Chat on WhatsApp"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaWhatsapp />
                        </a>
                        <button
                          type="button"
                          className={`icon-btn like ${isInWishlist(product.id) ? 'liked' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleWishlist(product);
                          }}
                          aria-label="Add to wishlist"
                          title="Add to wishlist"
                        >
                          <FiHeart />
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-primary card-btn add-to-cart"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(product);
                      }}
                    >
                      ADD TO CART
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="arrivals-actions">
            <Link to="/collection/silk-sarees" className="btn-primary">Explore Collection</Link>
          </div>
        </div>
      </section>

      <section className="collection-showcase">
        <div className="container">
          <div className="showcase-stage">
            {showcaseSlides.map((slide, index) => {
              const relative = (index - showcaseIndex + showcaseSlides.length) % showcaseSlides.length;
              const positionClass = relative === 0 ? 'is-center' : relative === 1 ? 'is-right' : 'is-left';
              return (
                <div key={slide.id} className={`showcase-card ${positionClass}`}>
                  <img src={slide.image} alt={slide.title} loading="lazy" />
                  <div className="showcase-overlay">
                    <div className="showcase-title">{slide.title}</div>
                    <div className="showcase-sub">{slide.tagline}</div>
                    <button type="button" className="showcase-btn">Shop Now</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      { }
      <section className="shop-by-category">
        <div className="container">
          <h2 className="category-title">SHOP BY CATEGORY</h2>
          <div className="category-grid">
            <div className="category-card">
              <div className="category-image">
                <img className="category-img formal" src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761843271/WhatsApp_Image_2025-10-30_at_21.48.25_78377178_xhcdlv.jpg" alt="Special Occasions Sarees" loading="lazy" />
              </div>
              <div className="category-label">FORMAL</div>
            </div>
            <div className="category-card">
              <div className="category-image">
                <img className="category-img party" src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761843271/WhatsApp_Image_2025-10-30_at_21.48.25_7b5df568_facggo.jpg" alt="Party Sarees" loading="lazy" />
              </div>
              <div className="category-label">PARTY</div>
            </div>
            <div className="category-card">
              <div className="category-image">
                <img className="category-img casual" src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761843271/WhatsApp_Image_2025-10-30_at_21.48.26_a9979924_oz0ydd.jpg" alt="Formal Sarees" loading="lazy" />"
              </div>
              <div className="category-label">CASUAL</div>
            </div>
            <div className="category-card">
              <div className="category-image">
                <img className="category-img traditional" src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761843271/WhatsApp_Image_2025-10-30_at_21.48.26_912a1562_aus7nx.jpg" alt="Casual Sarees" loading="lazy" />
              </div>
              <div className="category-label">TRADITIONAL</div>
            </div>
            <div className="category-card">
              <div className="category-image">
                <img className="category-img special" src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761843271/WhatsApp_Image_2025-10-30_at_21.48.26_577863cb_ruqp76.jpg" alt="Traditional Sarees" loading="lazy" />
              </div>
              <div className="category-label">SPECIAL OCCASIONS</div>
            </div>
          </div>
        </div>
      </section>

      { }
      <section className="celebrate-gift-section">
        <div className="container">
          <h2 className="celebrate-title">CELEBRATE & GIFT SAREES</h2>
          <div className="price-filters">
            <button className={`price-filter ${selectedPriceFilter === '5k' ? 'active' : ''}`} onClick={() => handlePriceFilterChange('5k')}>
              Under 5k
            </button>
            <button className={`price-filter ${selectedPriceFilter === '10k' ? 'active' : ''}`} onClick={() => handlePriceFilterChange('10k')}>
              Under 10k
            </button>
            <button className={`price-filter ${selectedPriceFilter === '20k' ? 'active' : ''}`} onClick={() => handlePriceFilterChange('20k')}>
              Under 20k
            </button>
            <button className={`price-filter ${selectedPriceFilter === '30k' ? 'active' : ''}`} onClick={() => handlePriceFilterChange('30k')}>
              Under 30k
            </button>
            <button className={`price-filter ${selectedPriceFilter === '40k' ? 'active' : ''}`} onClick={() => handlePriceFilterChange('40k')}>
              Under 40k
            </button>
          </div>
          <div className="gift-products-grid">
            {loadingCelebrate ? (
              <div className="celebrate-loading">
                <p>Loading celebrate products...</p>
              </div>
            ) : celebrateProducts.length === 0 ? (
              <div className="celebrate-empty">
                <p>No products found for the selected price range.</p>
              </div>
            ) : (
              celebrateProducts.slice(0, 8).map(product => (
                <div key={product.id} className="gift-product-card" onClick={() => navigate(`/product/${product.id}`)} style={{ cursor: 'pointer' }}>
                  <div className="gift-product-image">
                    <ImageCarousel
                      images={product.images && product.images.length > 0 ? product.images : ['https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761476953/Screenshot_2025-10-26_163521_xzl1r4.svg']}
                      autoPlay={true}
                      interval={4000}
                      showThumbnails={false}
                      showControls={false}
                      className="gift-product-carousel"
                    />
                  </div>
                  <div className="gift-product-info">
                    <div className="gift-product-title">{product.name || 'Silk Saree'}</div>
                    <div className="gift-product-price">₹{product.price.toLocaleString()}</div>
                    <div className="gift-product-actions-wrapper">
                      <div className="gift-product-actions">
                        <a
                          className="icon-btn whatsapp"
                          href={`https://wa.me/919591128327?text=${encodeURIComponent(`${product.image || (product.images && product.images[0])}\n\nHi! I am interested in ${product?.name || 'this saree'} priced at Rs ${product?.price?.toLocaleString() || 'N/A'}.\nView product: ${window.location.origin}/product/${product.slug || product.id}\nPlease provide more details.`)}`}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Chat on WhatsApp"
                          title="Chat on WhatsApp"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaWhatsapp />
                        </a>
                        <button
                          type="button"
                          className={`icon-btn like ${isInWishlist(product.id) ? 'liked' : ''}`}
                          onClick={(e) => { e.stopPropagation(); toggleWishlist(product); }}
                          aria-label="Add to wishlist"
                          title="Add to wishlist"
                        >
                          <FiHeart />
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-primary card-btn add-to-cart"
                      onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                    >
                      ADD TO CART
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      { }
      <section className="styled-celebrations">
        <div className="container">
          <h2 className="celebrations-title">STYLED FOR YOUR CELEBRATIONS</h2>
          <div className="celebrations-collage">
            {/* Collage Images (updated to use Cloudinary links) */}
            <div className="collage-image top-left">
              <img
                src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761839823/WhatsApp_Image_2025-10-30_at_21.22.30_bc24e22f_e2mrff.jpg"
                alt="Styled Saree - Top Left"
                loading="lazy"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.backgroundColor = '#e0e0e0'; }}
              />
            </div>
            <div className="collage-image top-center">
              <img
                src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761839823/WhatsApp_Image_2025-10-30_at_21.22.31_eb04a1b8_hfjpk3.jpg"
                alt="Styled Saree - Top Center"
                loading="lazy"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.backgroundColor = '#e0e0e0'; }}
              />
            </div>
            <div className="collage-image top-right">
              <img
                src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761839823/WhatsApp_Image_2025-10-30_at_21.22.31_adc8123f_vhgkbg.jpg"
                alt="Styled Saree - Top Right"
                loading="lazy"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.backgroundColor = '#e0e0e0'; }}
              />
            </div>
            <div className="collage-image middle-left">
              <img
                src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761839823/WhatsApp_Image_2025-10-30_at_21.22.32_44cd13d3_rnrt65.jpg"
                alt="Styled Saree - Middle Left"
                loading="lazy"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.backgroundColor = '#e0e0e0'; }}
              />
            </div>
            <div className="collage-image center-focal">
              <img
                src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761839824/WhatsApp_Image_2025-10-30_at_21.22.33_b79bb662_ljepcr.jpg"
                alt="Styled Saree - Center Focal"
                loading="lazy"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.backgroundColor = '#e0e0e0'; }}
              />
            </div>
            <div className="collage-image bottom-left">
              <img
                src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761839823/WhatsApp_Image_2025-10-30_at_21.22.32_c04e8ef1_wo0qvk.jpg"
                alt="Styled Saree - Bottom Left"
                loading="lazy"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.backgroundColor = '#e0e0e0'; }}
              />
            </div>
          </div>
        </div>
      </section>

      { }
      <section id="about" className="about-shanti-silks">
        <div className="container">
          <h2 className="celebrations-title">ABOUT SHANTI SILKS</h2>
          <div className="about-content">
            <div className="about-image">
              <img
                src="https://res.cloudinary.com/dbaiaiwkk/image/upload/v1761814287/Screenshot_2025-10-30_142049_nl2sci.png"
                alt="Shanti Silk House Logo"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.style.backgroundColor = '#f5f5f5';
                }}
              />
            </div>
            <div className="about-text">
              <h2 className="about-title">Shanti Silk House - Weaving Tradition, Draping Dreams</h2>
              <div className="about-paragraphs">
                <p>
                  Shanti Silk House is a women-led brand born from a deep love for India's rich weaving heritage.
                  Founded by a passionate female entrepreneur Darshini Gowda, her journey began with a dream –
                  to celebrate traditional sarees while keeping the essence of Indian culture alive in every thread.
                </p>
                <p>
                  Each saree at Shanti Silk House is handpicked and handcrafted by skilled artisans across India.
                  From the luxurious Kanchipuram silks to the intricate Banarasi weaves, every piece tells a story
                  of timeless craftsmanship and grace.
                </p>
                <p>
                  We believe a saree is more than just attire – it's an emotion, a legacy, and a symbol of empowerment.
                  Our mission is to preserve tradition while blending it beautifully with modern elegance, making every
                  woman feel confident, proud, and connected to her roots.
                </p>
                <div className="contact-details">
                  <p><strong>Visit us at:</strong> 3H5Q+X3C, 1st Main Rd, CQAL Layout, Sahakar Nagar, Byatarayanapura, Bengaluru, Karnataka</p>
                  <p><strong>Call us:</strong> +91 95911 28327</p>
                  <p><strong>WhatsApp:</strong> <a href="https://wa.me/919591128327" target="_blank" rel="noopener noreferrer" style={{ color: '#25D366', textDecoration: 'none' }}>+91 95911 28327</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      { }
      <section className="newsletter">
        <div className="container">
          <div className="newsletter-content">
            <h2>Stay Updated</h2>
            <p>Subscribe to our newsletter for exclusive offers and new arrivals</p>
            <div className="newsletter-form">
              <input type="email" placeholder="Enter your email address" />
              <button className="btn-primary">Subscribe</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
